import { inject, Injectable } from '@angular/core';
import { Contract } from '@models/contract';
import { isCurrentlyActive, isQueued, PaymentMethod } from '@models/payment-method';
import { InsurenetInterfaceService } from '@core';
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, tap } from 'rxjs';

type PaymentMethodsPerContractIdMap = Map<number, PaymentMethod[]>;

@Injectable({
    providedIn: 'root',
})
export class PaymentMethodsService {
    private _data$: BehaviorSubject<PaymentMethodsPerContractIdMap> =
        new BehaviorSubject<PaymentMethodsPerContractIdMap>(new Map<number, PaymentMethod[]>());

    public data$: Observable<PaymentMethodsPerContractIdMap> = this._data$.asObservable();

    private selectedContractId$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

    private insurenetInterfaceService: InsurenetInterfaceService = inject(InsurenetInterfaceService);

    public paymentMethodsForSelectedContract$: Observable<PaymentMethod[]> = combineLatest([
        this._data$,
        this.selectedContractId$,
    ]).pipe(
        map(([data, contractId]: [PaymentMethodsPerContractIdMap, number | null]) => {
            if (contractId !== null && data.has(contractId)) {
                return data.get(contractId);
            }
            return [];
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public selectContract(contract: Contract | number | null): void {
        if (typeof contract === 'object') {
            contract = contract.id;
        }
        this.selectedContractId$.next(contract);
    }

    /**
     * @description fetch all payment methods for a given contract
     * @param contract the contract or contract id to fetch for
     * @returns {Observable<PaymentMethod[]>} an observable of the payment
     * methods returned by the API
     */
    public fetch(contract: Contract | number): Observable<PaymentMethod[]> {
        if (typeof contract === 'object') {
            contract = contract.id;
        }
        return this.insurenetInterfaceService.getPaymentMethods(contract).pipe(
            tap((paymentMethods: PaymentMethod[]) => {
                const current = new Map(this._data$.getValue());
                current.set(contract, paymentMethods);
                this._data$.next(current);
            }),
        );
    }

    /**
     * @description create a new payment method and append it to the list of
     * payment methods for the contract. Only queued methods may be created. If
     * you want to change the currently active payment method please use the
     * {@link update} method. This method will throw an error when you try to
     * update a payment method that is not queued.
     * @see {@link isQueued}
     * @param {Omit<PaymentMethod, 'id'>} paymentMethod the payment method to create
     * @returns {Observable<PaymentMethod[]>} observable of the updated payment method
     * array for the contract.
     */
    public create(paymentMethod: Omit<PaymentMethod, 'id'>): Observable<PaymentMethod[]> {
        if (!isQueued(paymentMethod) && !isCurrentlyActive(paymentMethod)) {
            throw new Error(
                'PaymentMethodsService.create: Only queued PaymentMethods may be created. Use update to change the currently active PaymentMethod',
            );
        }
        const { contractId } = paymentMethod;

        return this.insurenetInterfaceService.createPaymentMethod(paymentMethod).pipe(
            tap((paymentMethods: PaymentMethod[]) => {
                const next = new Map(this._data$.getValue());
                next.set(contractId, paymentMethods);
                this._data$.next(next);
            }),
        );
    }

    /**
     * @description update a payment method. Only the currently active and queued
     * `PaymentMethod`s may be updated, so this method will throw an error when
     * you try to update a deprecated `PaymentMethod`.
     * @see {@link isQueued}
     * @see {@link isCurrentlyActive}
     * @param paymentMethod the payment method you want updated
     * @returns {Observable<PaymentMethod[]>} an observable of the payment
     * methods for the contract, including the updates.
     */
    public update(paymentMethod: PaymentMethod): Observable<PaymentMethod[]> {
        if (!isQueued(paymentMethod) && !isCurrentlyActive(paymentMethod)) {
            throw new Error('PaymentMethodsService.update: Only queued and active PaymentMethods may be updated');
        }
        const { contractId } = paymentMethod;

        return this.insurenetInterfaceService.updatePaymentMethod(paymentMethod).pipe(
            tap((paymentMethods: PaymentMethod[]) => {
                const next = new Map(this._data$.getValue());
                next.set(contractId, paymentMethods);
                this._data$.next(next);
            }),
        );
    }

    /**
     * @description deletes a payment method. Only queued `PaymentMethod`s may be
     * deleted, so this method will throw an error when you try to update the
     * currently active or a deprecated `PaymentMethod`.
     * @see {@link isQueued}
     * @param paymentMethod the payment method you want to delete
     * @returns {Observable<PaymentMethod[]>} an observable of the payment
     * methods for the contract, including the updates.
     */
    public delete(paymentMethod: PaymentMethod): Observable<PaymentMethod[]> {
        if (!isQueued(paymentMethod)) {
            throw new Error('PaymentMethodsService.delete: Only queued PaymentMethods may be deleted');
        }
        const { contractId } = paymentMethod;

        return this.insurenetInterfaceService.deletePaymentMethod(paymentMethod).pipe(
            tap((paymentMethods: PaymentMethod[]) => {
                const next = new Map(this._data$.getValue());
                next.set(contractId, paymentMethods);
                this._data$.next(next);
            }),
        );
    }

    /**
     * @description clear all data stored in the service
     */
    public clear(): void {
        this._data$.next(new Map<number, PaymentMethod[]>());
    }
}
